/**
 * F32 (Wave 2C) — admin suspend-open-game dialog.
 *
 * Mandatory reason (1–500 chars, validated against `SuspendOpenGameInputSchema`)
 * + a warning that the game will be hidden + non-joinable. Posts via the parent
 * page's `onSuspend` which calls `adminOpenGamesApi.suspendGame`.
 *
 * Gated by the parent page on `open_games:moderate`. Composes F02 primitives
 * only (Modal / FormField / Textarea / Button / Banner).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea } from '@bravemove/ui-web';
import { SuspendOpenGameInputSchema, OPEN_GAME_SUSPENSION_REASON_MAX } from '@bravemove/types';

export interface SuspendOpenGameDialogProps {
  readonly open: boolean;
  readonly onSuspend: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function SuspendOpenGameDialog({
  open,
  onSuspend,
  onClose,
  errorMessage,
}: SuspendOpenGameDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = useMemo(() => SuspendOpenGameInputSchema.safeParse({ reason }).success, [reason]);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSuspend(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('open-game:admin.suspend_dialog.title', { defaultValue: 'Suspend this game?' })}
      description={t('open-game:admin.suspend_dialog.body', {
        defaultValue:
          'It will be hidden from the marketplace and no one can join until you unsuspend it.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="suspend-open-game-cancel"
          >
            {t('open-game:admin.suspend_dialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="suspend-open-game-submit"
          >
            {t('open-game:admin.suspend_dialog.confirm', { defaultValue: 'Suspend' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="suspend-open-game-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('open-game:admin.suspend_dialog.reason_label', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('open-game:admin.suspend_dialog.reason_placeholder', {
              defaultValue: 'Why are you suspending this game?',
            })}
            rows={4}
            maxLength={OPEN_GAME_SUSPENSION_REASON_MAX}
            data-testid="suspend-open-game-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
